<template>
<div class="contents">
  <div class="vehicle-form-box">
    <div class="err-msg-area" v-if="errorMessage">
      <span class="font_orange">{{ errorMessage }}</span>
    </div>

    <table class="tbl">
      <colgroup>
        <col style="width: 32%;">
        <col style="width: 68%;">
      </colgroup>
      <tr>
        <th class="tbl-cell tbl-header-cell" colspan="2">車両情報</th>
      </tr>
      <tr>
        <td class="tbl-cell">車両ID</td>
        <td class="tbl-cell">
          <span v-if="!isCreateMode">{{ currentVehicleId }}</span>
        </td>
      </tr>
      <tr>
        <td class="tbl-cell">車両名</td>
        <td class="tbl-cell"><input v-model="formValues.name" type="text" class="form-control-sm" /></td>
      </tr>
      <tr>
        <td class="tbl-cell">登録番号</td>
        <td class="tbl-cell"><input v-model="formValues.registrationNumber" type="text" class="form-control-sm" /></td>
      </tr>
      <tr>
        <td class="tbl-cell">積載量</td>
        <td class="tbl-cell"><input v-model="formValues.capacity" type="text" class="form-control-sm" /></td>
      </tr>
      <tr>
        <td class="tbl-cell">所有者</td>
        <td class="tbl-cell"><input v-model="formValues.owner" type="text" class="form-control-sm" /></td>
      </tr>
      <tr>
        <td class="tbl-cell">時間単価</td>
        <td class="tbl-cell"><input v-model="formValues.costPerHour" type="text" class="form-control-sm" /></td>
      </tr>
      <tr>
        <td class="tbl-cell">距離単価</td>
        <td class="tbl-cell"><input v-model="formValues.costPerKilometer" type="text" class="form-control-sm" /></td>
      </tr>
    </table>
  </div>

  <div class="vehicle-form-buttons">
    <p class="submit_bx_l">
      <button class="submit1 bg_gray" @click="backToList">
        戻る
      </button>
    </p>
    <p class="submit_bx_r">
      <button class="submit1 bg_blue" @click="saveVehicle">
        {{ submitButtonLabel }}
      </button>
    </p>
  </div>
</div>
</template>

<script>
import { AdminPageType } from '@/consts/admin'
import adminVehicleApi from '@/api/adminVehicle'
import { getApiErrorMessage } from '@/utils/apiErrorHelper'
import { useAdminPageAccess } from '@/composables/useAdminPageAccess'

export default {
  name: 'admin-vehicles-form',
  setup(props) {
    const { ensureManagerAccess } = useAdminPageAccess(props.adminType)
    return { ensureManagerAccess }
  },
  props: {
    adminType: {
      type: String,
      default: AdminPageType.ADMIN
    },
    formMode: {
      type: String,
      required: true,
      validator: value => ['create', 'edit'].includes(value)
    },
    vehicleId: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      currentVehicleId: null,
      errorMessage: '',
      formValues: {
        name: '',
        registrationNumber: '',
        capacity: '',
        owner: '',
        costPerHour: '1.0',
        costPerKilometer: '1.0',
      }
    }
  },
  computed: {
    isCreateMode() {
      return this.formMode === 'create'
    },
    submitButtonLabel() {
      return this.isCreateMode ? '登録' : '変更'
    }
  },
  async mounted() {
    try {
      const canAccessPage = await this.ensureManagerAccess()
      if (!canAccessPage) return

      if (this.isCreateMode) return

      await this.fetchVehicle()
    } catch (error) {
      this.errorMessage = '車両情報画面の初期化に失敗しました。'
    }
  },
  methods: {
    async fetchVehicle() {
      if (!this.vehicleId) {
        this.backToList()
        return
      }

      try {
        const { data: vehicleResponse } = await adminVehicleApi.get(this.vehicleId)
        this.currentVehicleId = vehicleResponse.id
        this.formValues = {
          name: vehicleResponse.vehicle_name,
          registrationNumber: vehicleResponse.registration_number,
          capacity: String(vehicleResponse.payload_capacity),
          owner: vehicleResponse.owner_name,
          costPerHour: String(vehicleResponse.cost_per_hour),
          costPerKilometer: String(vehicleResponse.cost_per_kilometer),
        }
      } catch (error) {
        this.errorMessage = getApiErrorMessage(error, '車両情報の取得に失敗しました。')
        if (error?.response?.status === 404) {
          this.backToList()
        }
      }
    },
    backToList() {
      const routeName = this.adminType === AdminPageType.STAFF ? 'staff-vehicles' : 'admin-vehicles'
      this.$router.push({ name: routeName })
    },
    async saveVehicle() {
      this.errorMessage = ''

      const name = this.formValues.name.trim()
      const registrationNumber = this.formValues.registrationNumber.trim()
      const owner = this.formValues.owner.trim()

      if (!name) {
        this.errorMessage = '車両名を入力してください。'
        return
      }
      if (!registrationNumber) {
        this.errorMessage = '登録番号を入力してください。'
        return
      }
      if (this.formValues.capacity === '' || this.formValues.capacity === null) {
        this.errorMessage = '積載量を入力してください。'
        return
      }
      const capacity = Number(this.formValues.capacity)
      if (!Number.isFinite(capacity) || capacity < 0) {
        this.errorMessage = '積載量は0以上の数値で入力してください。'
        return
      }
      if (!owner) {
        this.errorMessage = '所有者を入力してください。'
        return
      }
      if (this.formValues.costPerHour === '' || this.formValues.costPerHour === null) {
        this.errorMessage = '時間単価を入力してください。'
        return
      }
      const costPerHour = Number(this.formValues.costPerHour)
      if (!Number.isFinite(costPerHour) || costPerHour < 0) {
        this.errorMessage = '時間単価は0以上の数値で入力してください。'
        return
      }
      if (!/^\d+(\.\d{1,2})?$/.test(this.formValues.costPerHour)) {
        this.errorMessage = '時間単価は小数点2桁以内で入力してください。'
        return
      }
      if (this.formValues.costPerKilometer === '' || this.formValues.costPerKilometer === null) {
        this.errorMessage = '距離単価を入力してください。'
        return
      }
      const costPerKilometer = Number(this.formValues.costPerKilometer)
      if (!Number.isFinite(costPerKilometer) || costPerKilometer < 0) {
        this.errorMessage = '距離単価は0以上の数値で入力してください。'
        return
      }
      if (!/^\d+(\.\d{1,2})?$/.test(this.formValues.costPerKilometer)) {
        this.errorMessage = '距離単価は小数点2桁以内で入力してください。'
        return
      }

      const payload = {
        vehicle_name: name,
        registration_number: registrationNumber,
        payload_capacity: capacity,
        owner_name: owner,
        cost_per_hour: costPerHour,
        cost_per_kilometer: costPerKilometer,
      }

      if (this.isCreateMode) {
        try {
          await adminVehicleApi.create(payload)
          this.backToList()
        } catch (error) {
          this.errorMessage = getApiErrorMessage(error, '車両情報の登録に失敗しました。')
        }
        return
      }

      try {
        await adminVehicleApi.update(this.vehicleId, payload)
        this.backToList()
      } catch (error) {
        this.errorMessage = getApiErrorMessage(error, '車両情報の更新に失敗しました。')
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.contents {
  width: 95%;
  margin: 20px auto 0;
  min-height: 400px;
}

.vehicle-form-box {
  width: 420px;
}

.err-msg-area {
  margin-bottom: 10px;
}

.tbl {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 16px;
  background-color: #ffffff;

  .tbl-cell {
    border: 1px solid #b8c0cc;
    padding: 8px 10px;
  }

  .tbl-header-cell {
    text-align: center;
    background-color: #dce7f8;
    font-weight: 700;
  }
}

.form-control-sm {
  width: 100%;
  max-width: 240px;
  font-size: 16px;
}

.vehicle-form-buttons {
  width: 500px;
  max-width: 100%;
  overflow: hidden;
}
</style>
